import React, { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AuthModal } from "@features/auth";
import { AboutPage } from "@pages/about";
import { YandexCallbackPage } from "@pages/auth";
import { BlogIndexPage, BlogPostPage } from "@pages/blog";
import {
  CatalogPage,
  EXPORTS_CATALOG,
  FORMATS_CATALOG,
  LANGUAGES_CATALOG,
  PLATFORMS_CATALOG,
  USE_CASES_CATALOG
} from "@pages/catalog";
import { ContactsPage } from "@pages/contacts";
import { MainPage } from "@pages/main";
import { NotFoundPage } from "@pages/not-found";
import { OfferPage } from "@pages/offer";
import { PricingPage } from "@pages/pricing";
import { PrivacyPage } from "@pages/privacy";
import { TelegramPage } from "@pages/telegram";
import { TgRedirectPage } from "@pages/tg-redirect";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { BLOG_POSTS } from "@shared/config/blog-posts";
import { SEO_PAGES } from "@shared/config/seo-pages";
import { AuthProvider } from "@shared/contexts/auth-context";
import { HashScroller } from "@shared/helper_lib/HashScroller";
import { ScrollToTop } from "@shared/helper_lib/ScrollToTop";
import { useAdAttribution } from "@shared/hooks/useAdAttribution";
import { BalanceWebSocketProvider } from "@shared/providers/BalanceWebSocketProvider";
import { TranscriptionWebSocketProvider } from "@shared/providers/TranscriptionWebSocketProvider";
import { NoIndexMeta } from "@shared/ui/components/no-index-meta";
import { ProtectedRoute } from "@shared/ui/components/ProtectedRoute";
import { RouteErrorBoundary } from "@shared/ui/components/route-error-boundary";
import { Toaster } from "@shared/ui/components/toaster";
import { LoadingDots } from "@shared/ui/LoadingDots";
import { CookieConsent } from "@widgets/CookieConsent/CookieConsent";
import { Footer } from "@widgets/Footer";
import { Header } from "@widgets/Header";
import { UploadProgress } from "@widgets/UploadProgress/UploadProgress";

import "./styles/fonts.css";
import "./styles/globals.css";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

/** Хуку нужен контекст авторизации, поэтому он живёт отдельным компонентом внутри AuthProvider */
const AdAttributionTracker: React.FC = () => {
  useAdAttribution();
  return null;
};

/**
 * Приватные разделы грузим по требованию — они не пререндерятся и не нужны
 * посетителю с поиска. Админка вдобавок тянет за собой много кода,
 * а история — библиотеку docx для экспорта.
 *
 * Публичные страницы (MainPage, CatalogPage, Pricing, Telegram, Privacy, Offer, Contacts)
 * остаются eager: они отдаются готовым HTML из пререндера, и отложенная загрузка
 * дала бы мелькание — фолбэк вместо уже отрисованного содержимого.
 *
 * YandexCallbackPage тоже eager: он крошечный и стоит на критическом пути входа,
 * где лишняя загрузка чанка означает ожидание на пустом экране.
 */
const AdminPage = lazy(() => import("@pages/admin").then(m => ({ default: m.AdminPage })));
const HistoryPage = lazy(() => import("@pages/history").then(m => ({ default: m.HistoryPage })));
const SubscriptionPage = lazy(() => import("@pages/subscription").then(m => ({ default: m.SubscriptionPage })));
const PaymentSuccessPage = lazy(() => import("@pages/payment").then(m => ({ default: m.PaymentSuccessPage })));
const PaymentFailPage = lazy(() => import("@pages/payment").then(m => ({ default: m.PaymentFailPage })));

export const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <BalanceWebSocketProvider>
          <TranscriptionWebSocketProvider>
            <BrowserRouter>
              <AdAttributionTracker />
              <NoIndexMeta />
              <ScrollToTop />
              <HashScroller />
              <div className="flex min-h-screen flex-col bg-background">
                <Header />
                <main className="flex-grow">
                  {/* Границы Suspense хватает одной: на публичных страницах ленивых
                      компонентов нет, поэтому она просто не срабатывает.
                      ErrorBoundary снаружи обязателен — без него сорвавшаяся загрузка
                      чанка обрушила бы всё дерево до белого экрана */}
                  <RouteErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="flex min-h-[60vh] items-center justify-center">
                          <LoadingDots />
                        </div>
                      }
                    >
                      <Routes>
                        <Route path="/" element={<MainPage />} />
                        {SEO_PAGES.map(page => (
                          <Route
                            key={page.path}
                            path={page.path}
                            element={
                              <MainPage
                                customTitle={
                                  typeof page.customTitle === "string" ? (
                                    <>
                                      <span className="text-primary font-rublev">{page.customTitle.charAt(0)}</span>
                                      {page.customTitle.slice(1)}
                                    </>
                                  ) : (
                                    page.customTitle
                                  )
                                }
                                customSubtitle={page.customSubtitle}
                                metaTitle={page.metaTitle}
                                metaDescription={page.metaDescription}
                                faq={page.faq}
                                seoDescription={page.seoDescription}
                                cluster={page.cluster}
                                canonicalPath={page.canonicalPath}
                                relatedArticles={page.relatedArticles}
                              />
                            }
                          />
                        ))}
                        <Route path="/blog" element={<BlogIndexPage />} />
                        {BLOG_POSTS.map(post => (
                          <Route key={post.slug} path={`/blog/${post.slug}`} element={<BlogPostPage post={post} />} />
                        ))}
                        <Route path="/formats" element={<CatalogPage {...FORMATS_CATALOG} />} />
                        <Route path="/languages" element={<CatalogPage {...LANGUAGES_CATALOG} />} />
                        <Route path="/platforms" element={<CatalogPage {...PLATFORMS_CATALOG} />} />
                        <Route path="/use-cases" element={<CatalogPage {...USE_CASES_CATALOG} />} />
                        <Route path="/exports" element={<CatalogPage {...EXPORTS_CATALOG} />} />
                        <Route path="/history" element={<HistoryPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/telegram" element={<TelegramPage />} />
                        <Route path="/tg" element={<TgRedirectPage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="/payment/subscription/success" element={<PaymentSuccessPage />} />
                        <Route path="/payment/minutes/success" element={<PaymentSuccessPage />} />
                        <Route path="/payment/fail" element={<PaymentFailPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contacts" element={<ContactsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/offer" element={<OfferPage />} />
                        <Route
                          path="/console"
                          element={
                            <ProtectedRoute requireRole="superadmin">
                              <AdminPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/auth/yandex/callback" element={<YandexCallbackPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </Suspense>
                  </RouteErrorBoundary>
                </main>
                <Footer />
                <AuthModal />
                <Toaster />
                <UploadProgress />
                <CookieConsent />
              </div>
            </BrowserRouter>
          </TranscriptionWebSocketProvider>
        </BalanceWebSocketProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
};
